import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCloudArrowDown } from "@fortawesome/pro-light-svg-icons";
import {
  faBullhorn,
  faCalendar,
  faFile,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SpinnerSwap } from "../spinner";
import { SpaceAvatar } from "./spaceAvatar";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { formatDateDDMMYYYY } from "../../utils/date";
import { formatFileSize } from "../../utils/fileFormat";

export type SpaceHeaderProps = {
  /** The published space's display name (rendered inside the localized title). */
  spaceName: string;
  /** Who published the space — the "Provided by …" subtitle; hidden if absent. */
  provider?: string;
  /** Total number of files in the space, shown in the meta row. */
  fileCount: number;
  /** Total byte size of the space, shown in the download action's tooltip. */
  totalSize: number;
  /** When the space content was last updated — the "Updated on …" meta item. */
  modifiedAt?: Date;
  /** Avatar image URL; loaded via the guest cookie with a folder-glyph fallback. */
  avatarUrl?: string | null;
  /** Blurhash of the space avatar; placeholder while the image loads. */
  blurhash?: string | null;
  /** Download the whole space as a ZIP. When provided, renders the header CTA. */
  onDownloadAll?: () => void;
};

/**
 * The desktop published-space header card.
 *
 * Appearance follows the Adobe XD spec (gradient from `--primary-light-color`
 * to white). It shows the space avatar, the title ("Public Space …"), the
 * provider subtitle and a meta row (publicly-shared · file count · last-updated
 * date). Built from MUI primitives so it inherits the app theme.
 */
export function SpaceHeader({
  spaceName,
  provider,
  fileCount,
  totalSize,
  modifiedAt,
  avatarUrl,
  blurhash,
  onDownloadAll,
}: SpaceHeaderProps) {
  const { t, i18n } = useTranslation();
  // Show a spinner in the CTA while the (whole-space ZIP) download is prepared.
  const download = useAsyncAction(onDownloadAll ?? (() => {}));
  // Full localized date + time for the "Updated on" tooltip. The space payload
  // has no update timestamp of its own, so `modifiedAt` is the newest file's
  // modification time (see resolvePublishedSpace) — the best proxy available.
  const modifiedAtFull = modifiedAt
    ? modifiedAt.toLocaleString(i18n.language, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        gap: { xs: 2, sm: 3 },
        p: { xs: 2.5, sm: 3.5 },
        borderRadius: "14px",
        border: "1px solid rgba(218,218,218,0.5)", // surface dim #DADADA @ 50%
        // Primary-light at 50% opacity fading to white.
        background:
          "linear-gradient(89deg, color-mix(in srgb, var(--primary-light-color) 50%, transparent) 0%, #ffffff 100%)",
      }}
    >
      <SpaceAvatar
        url={avatarUrl}
        size={{ xs: 72, sm: 92 }}
        iconFontSize={32}
        blurhash={blurhash}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          component="h1"
          sx={{
            fontWeight: 400,
            fontSize: 20,
            lineHeight: "25px",
            color: "#1a1a1a",
            // A long / unbreakable space name must wrap inside the box (which can
            // shrink below the word width via minWidth:0) instead of spilling
            // right under the download button.
            overflowWrap: "break-word",
            // Clears the absolutely positioned download button; the rows
            // below keep the header's full width.
            pr: { xs: "52px", sm: "44px" },
          }}
        >
          {t('Public Space "{{name}}"', { name: spaceName })}
        </Typography>

        {provider && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 12,
              lineHeight: "15px",
              color: "#747474",
              overflowWrap: "break-word",
            }}
          >
            {t("Provided by {{provider}}", { provider })}
          </Typography>
        )}

        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 1.5, columnGap: 2, rowGap: 0.75 }}
        >
          <MetaItem icon={faBullhorn}>{t("Publicly shared")}</MetaItem>
          <MetaItem icon={faFile}>
            {fileCount === 1
              ? t("1 file")
              : t("{{count}} files", { count: fileCount })}
          </MetaItem>
          {modifiedAt && (
            <Tooltip title={modifiedAtFull} placement="top">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <MetaItem icon={faCalendar}>
                  {`${t("Updated on")} ${formatDateDDMMYYYY(modifiedAt)}`}
                </MetaItem>
              </Box>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {onDownloadAll && (
        // Icon-only action pinned to the header's top-right corner (20px in from
        // both edges). The tooltip carries the full label plus the space size.
        <Tooltip
          title={`${t("Download entire Space")} (${formatFileSize(
            totalSize,
            i18n.language
          )})`}
          placement="top"
        >
          <IconButton
            onClick={download.run}
            aria-label={t("Download entire Space")}
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              width: 36,
              height: 36,
              color: "#747474",
            }}
          >
            <SpinnerSwap loading={download.loading} spinnerColor="#747474">
              <FontAwesomeIcon
                icon={faCloudArrowDown as IconProp}
                style={{ fontSize: 20 }}
              />
            </SpinnerSwap>
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
}

/** One icon + label pair in the header's meta row. */
function MetaItem({ icon, children }: { icon: IconProp; children: ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ fontSize: 12, lineHeight: "15px", color: "#1a1a1a" }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 12 }} />
      <span>{children}</span>
    </Stack>
  );
}
